import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {Pagination} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {Fragment, type MouseEvent, type ReactNode} from 'react'

import {AIResourceResults, type AIResourceResult} from './AIResourceResults'
import styles from './AIResourceSearchPage.module.css'
import {ResourceTypeFilter, type ResourceTypeFilterProps} from './ResourceTypeFilter'
import {SearchCategoryNav} from './SearchCategoryNav'
import {SearchResultsHeader} from './SearchResultsHeader'
import type {
  AIResourceNoResultsProps,
  AIResourceSearchCategory,
  AIResourceSearchError,
  AIResourceSearchKind,
  AIResourceSearchKindCount,
} from './types'

export type AIResourceSearchPageState =
  | {
      status: 'success'
      results: readonly AIResourceResult[]
      totalCount?: number
      currentPage?: number
      pageCount?: number
      getPageHref?: (page: number) => string
    }
  | {status: 'loading'}
  | {status: 'empty'}
  | {status: 'error'; errors: readonly AIResourceSearchError[]}

export type AIResourceSearchPageProps = {
  categories: readonly AIResourceSearchCategory[]
  elapsedMilliseconds?: number
  filters: ResourceTypeFilterProps
  isLoadingCounters: boolean
  onPageChange?: (event: MouseEvent, page: number) => void
  query: string
  renderError: (error: AIResourceSearchError, index: number) => ReactNode
  renderNoResults: (props: AIResourceNoResultsProps) => ReactNode
  resultKind: AIResourceSearchKind
  searchKinds: AIResourceSearchKindCount[]
  state: AIResourceSearchPageState
}

export function AIResourceSearchPage({
  categories,
  elapsedMilliseconds,
  filters,
  isLoadingCounters,
  onPageChange,
  query,
  renderError,
  renderNoResults,
  resultKind,
  searchKinds,
  state,
}: AIResourceSearchPageProps) {
  return (
    <FilteredListPageLayout>
      <FilteredListPageLayout.Header>
        <FilteredListPageLayout.PageHeader>
          <FilteredListPageLayout.PageHeader.TitleArea>
            <FilteredListPageLayout.PageHeader.Title as="h1" className="sr-only">
              Search results
            </FilteredListPageLayout.PageHeader.Title>
          </FilteredListPageLayout.PageHeader.TitleArea>
        </FilteredListPageLayout.PageHeader>
      </FilteredListPageLayout.Header>

      <FilteredListPageLayout.Sidebar
        aria-label="Search filters"
        dialogTitle="Search filters"
        expandedWidth="medium"
        scrollable
      >
        <SearchCategoryNav categories={categories} />
        <ResourceTypeFilter {...filters} />
      </FilteredListPageLayout.Sidebar>

      <FilteredListPageLayout.Content as="div">
        <SearchResultsHeader
          elapsedMilliseconds={elapsedMilliseconds}
          errorMessage={state.status === 'error' ? state.errors[0]?.message : undefined}
          query={query}
          status={state.status}
          totalCount={state.status === 'success' ? state.totalCount : undefined}
        />
        {state.status === 'error' ? (
          <div className={styles.Notices}>
            {state.errors.slice(0, 2).map((error, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- matches the shared Search error list
              <Fragment key={index}>{renderError(error, index)}</Fragment>
            ))}
          </div>
        ) : null}
        <FilteredListPageLayout.Results aria-label="AI resource results">
          <ResultsContent
            isLoadingCounters={isLoadingCounters}
            onPageChange={onPageChange}
            renderNoResults={renderNoResults}
            resultKind={resultKind}
            searchKinds={searchKinds}
            state={state}
          />
        </FilteredListPageLayout.Results>
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}

function ResultsContent({
  isLoadingCounters,
  onPageChange,
  renderNoResults,
  resultKind,
  searchKinds,
  state,
}: {
  isLoadingCounters: boolean
  onPageChange?: (event: MouseEvent, page: number) => void
  renderNoResults: (props: AIResourceNoResultsProps) => ReactNode
  resultKind: AIResourceSearchKind
  searchKinds: AIResourceSearchKindCount[]
  state: AIResourceSearchPageState
}) {
  switch (state.status) {
    case 'success':
      return <SuccessResults onPageChange={onPageChange} state={state} />
    case 'loading':
      return <LoadingResults />
    case 'empty':
    case 'error':
      return renderNoResults({resultKind, searchKinds, isLoadingCounters})
  }
}

function SuccessResults({
  onPageChange,
  state,
}: {
  onPageChange?: (event: MouseEvent, page: number) => void
  state: Extract<AIResourceSearchPageState, {status: 'success'}>
}) {
  const {currentPage, getPageHref, pageCount} = state
  const showPagination = currentPage !== undefined && pageCount !== undefined && pageCount > 1 && getPageHref

  return (
    <>
      <AIResourceResults results={state.results} />
      {showPagination && (
        <div className={styles.Pagination}>
          <Pagination
            currentPage={currentPage}
            hrefBuilder={getPageHref}
            onPageChange={onPageChange}
            pageCount={pageCount}
          />
        </div>
      )}
    </>
  )
}

function LoadingResults() {
  return (
    <div className={styles.LoadingList} aria-hidden="true">
      <LoadingResultRow />
      <LoadingResultRow />
      <LoadingResultRow />
    </div>
  )
}

function LoadingResultRow() {
  return (
    <div className={styles.LoadingRow}>
      <SkeletonText size="titleMedium" />
      <SkeletonText size="bodyMedium" lines={2} />
    </div>
  )
}
